import React from 'react';
import { motion } from 'framer-motion';
import { Radar, CheckCircle2, ArrowRight } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';
import { DecryptedText } from './effects/DecryptedText';
import { ShinyText } from './effects/ShinyText';
import { ClickSpark } from './effects/ClickSpark';
import { Magnet } from './effects/Magnet';
export function ComingSoon() {
  // Formspree handles email capture — submissions land in the Formspree dashboard
  // and trigger any notifications configured there.
  const [state, handleSubmit] = useForm('mykvgpkn');
  const submitting = state.submitting;
  const succeeded = state.succeeded;
  return <ClickSpark sparkColor="#1a8fd4" sparkCount={10} sparkRadius={22} sparkSize={12} duration={600} data-id="element-1">
      <div className="relative min-h-screen w-full bg-[#05070a] text-white overflow-hidden flex flex-col items-center selection:bg-[#1a8fd4]/30" data-id="element-2">
        {/* SVG filter: removes white/near-white pixels from the logo JPG and
                inverts the remaining dark wordmark to white so it reads on dark bg.
                This makes the logo's background TRULY transparent so the radar
                animation behind it shows through. */}
        <svg width="0" height="0" className="absolute" aria-hidden="true" data-id="element-3">
          <defs data-id="element-4">
            <filter id="periscan-remove-white" colorInterpolationFilters="sRGB" data-id="element-5">
              {/* Step 1: invert colors — white→black, black→white, blue→orange */}
              <feColorMatrix type="matrix" values="-1 0 0 0 1
                       0 -1 0 0 1
                       0 0 -1 0 1
                       0 0 0 1 0" data-id="element-6" />
              {/* Step 2: hue-rotate ~180° to restore the brand blue */}
              <feColorMatrix type="hueRotate" values="180" data-id="element-7" />
              {/* Step 3: use the (now dark) inverted-white as alpha — pixels that
               were pure white become fully transparent; everything else stays. */}
              <feColorMatrix type="matrix" values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      1 1 1 0 0" data-id="element-8" />
            </filter>
          </defs>
        </svg>
        {/* Background Texture */}
        <div className="absolute inset-0 bg-dot-grid opacity-30 pointer-events-none" data-id="element-9" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05070a] via-transparent to-[#05070a] pointer-events-none" data-id="element-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05070a] via-transparent to-[#05070a] pointer-events-none" data-id="element-11" />

        {/* Radar Animation Centerpiece */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden" data-id="element-12">
          {/* Sweeping Radar Line */}
          <motion.div className="absolute rounded-full opacity-40" style={{
          width: '120vmax',
          height: '120vmax',
          background: 'conic-gradient(from 0deg, transparent 0%, transparent 80%, rgba(26,143,212,0.1) 98%, rgba(26,143,212,0.6) 100%)'
        }} animate={{
          rotate: 360
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-13" />

          {/* Concentric Pulses */}
          {[0, 1, 2, 3].map(i => <motion.div key={i} className="absolute rounded-full border border-[#1a8fd4]" style={{
          width: '200px',
          height: '200px'
        }} animate={{
          scale: [1, 6],
          opacity: [0.6, 0],
          borderWidth: ['2px', '1px']
        }} transition={{
          duration: 4,
          repeat: Infinity,
          delay: i * 1,
          ease: 'linear'
        }} data-id="element-14" />)}

          {/* Center Node */}
          <div className="absolute w-4 h-4 bg-[#1a8fd4] rounded-full shadow-[0_0_20px_#1a8fd4] animate-pulse" data-id="element-15" />
        </div>

        {/* Main Content */}
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-3xl px-6 py-16 text-center" data-id="element-16">
          {/* Logo */}
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="mb-12" data-id="element-17">
            <img src="https://cdn.magicpatterns.com/uploads/19wZLyvpjSe2tkaaA1WT1m/p_logo_02_(1)_(1).jpg" alt="Periscan" className="h-20 md:h-28 w-auto object-contain drop-shadow-[0_0_25px_rgba(26,143,212,0.35)]" style={{
            filter: 'url(#periscan-remove-white)'
          }} data-id="element-18" />
          </motion.div>

          {/* Typography */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} className="space-y-6 mb-12" data-id="element-19">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1a8fd4]/10 border border-[#1a8fd4]/20 text-sm font-medium tracking-wide backdrop-blur-sm" data-id="element-20">
              <Radar className="w-4 h-4 text-[#1a8fd4]" data-id="element-21" />
              <ShinyText text="REBORN FOR THE MODERN ERA" speed={5} data-id="element-22" />
            </div>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight" data-id="element-23">
              <DecryptedText text="The Hacker On Your Side." speed={45} maxIterations={18} delay={300} parentClassName="text-transparent bg-clip-text bg-gradient-to-b from-white to-white/60" className="text-transparent bg-clip-text bg-gradient-to-b from-white to-white/60" encryptedClassName="text-[#1a8fd4]/70" data-id="element-24" />
            </h1>

            <p className="text-lg md:text-xl text-gray-400 max-w-xl mx-auto font-light leading-relaxed" data-id="element-25">
              The pioneer of Continuous Security Validation is being reborn for
              the modern era. Lock in your coordinates to be the first to know
              when we go live.
            </p>
          </motion.div>

          {/* Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4,
          ease: 'easeOut'
        }} className="w-full max-w-md" data-id="element-26">
            {succeeded ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-[#1a8fd4]/10 border border-[#1a8fd4]/20 backdrop-blur-md" data-id="element-27">
                <CheckCircle2 className="w-10 h-10 text-[#1a8fd4]" data-id="element-28" />
                <div className="space-y-1" data-id="element-29">
                  <h3 className="text-lg font-medium text-white" data-id="element-30">
                    Coordinates locked.
                  </h3>
                  <p className="text-sm text-gray-400" data-id="element-31">
                    We'll ping your radar when we're live.
                  </p>
                </div>
              </motion.div> : <div className="space-y-3" data-id="element-32">
                <form onSubmit={handleSubmit} className="relative flex items-center group" data-id="element-33">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#1a8fd4]/20 to-transparent rounded-full blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" data-id="element-34" />
                  <label htmlFor="email" className="sr-only" data-id="element-35">
                    Email address
                  </label>
                  <input id="email" type="email" name="email" required placeholder="Enter your email address" disabled={submitting} className="w-full bg-[#0a0f14]/80 border border-white/10 rounded-full py-4 pl-6 pr-32 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#1a8fd4]/50 focus:ring-1 focus:ring-[#1a8fd4]/50 backdrop-blur-md transition-all disabled:opacity-50" data-id="element-36" />
                  <Magnet padding={60} magnetStrength={4} className="absolute right-2 top-2 bottom-2" innerClassName="h-full" data-id="element-37">
                    <button type="submit" disabled={submitting} className="h-full px-6 bg-[#1a8fd4] hover:bg-[#1578b3] text-white rounded-full font-medium transition-colors flex items-center gap-2 disabled:opacity-80 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(26,143,212,0.4)] hover:shadow-[0_0_25px_rgba(26,143,212,0.6)]" data-id="element-38">
                      {submitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-39" /> : <>
                          <span data-id="element-40">Notify Me</span>
                          <ArrowRight className="w-4 h-4" data-id="element-41" />
                        </>}
                    </button>
                  </Magnet>
                </form>
                <ValidationError field="email" errors={state.errors} className="flex items-center justify-center gap-2 text-sm text-red-400/90" data-id="element-42" />
              </div>}
          </motion.div>
        </main>

        {/* Footer */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.8
      }} className="relative z-10 w-full pb-8 px-6 text-center text-xs text-gray-600 font-medium tracking-widest uppercase" data-id="element-43">
          © {new Date().getFullYear()} Periscan. All rights reserved.
        </motion.div>
      </div>
    </ClickSpark>;
}